/**
 * CONFIGURAZIONE DELL'AZIONE "invia email" (`crm.send-email`).
 *
 * ## Il destinatario è una SORGENTE, non un indirizzo
 *
 * Al momento in cui si configura non esiste ancora un record: l'automazione scatterà su preventivi
 * o contatti che non si conoscono, quindi non si può scegliere un indirizzo — si sceglie **come
 * arrivarci**. Le tre sorgenti riusano il contesto che l'entità già dichiara:
 *
 * - `RECORD` &mdash; l'indirizzo del record che ha fatto scattare l'automazione;
 * - `RELATED` &mdash; quelli dei record che `AutomationObjectType.getRelatedRecords` restituisce,
 *   cioè il contesto: per un preventivo l'azienda e i referenti;
 * - `FIXED` &mdash; un indirizzo digitato qui, per le automazioni che avvisano sempre la stessa
 *   persona.
 *
 * A risolvere una sorgente in indirizzi sarà il motore, leggendo gli `EmailAddress` di Liferay
 * agganciati al record per `className`/`classPK`. Ne segue che `RECORD` e `RELATED` possono
 * produrre **più di un destinatario**, o nessuno.
 *
 * ## Perché questa volta il vocabolario è condiviso davvero
 *
 * I codici delle sorgenti li conosce anche Java, a differenza dei preset di scadenza dell'azione
 * "crea attività" — che erano una comodità del form e si riducevano a un numero. Qui il codice
 * **è** il dato: non esiste un valore più semplice in cui collassarlo, perché il motore deve sapere
 * quale strada percorrere per trovare l'indirizzo. Aggiungere una sorgente è quindi una modifica
 * che tocca i due lati, ed è corretto che lo sia.
 *
 * ## Il mittente è un DATO, e per questo scende dal server
 *
 * Le sorgenti qui sopra sono un vocabolario: finite, note quando si compila, scritte nei due lati.
 * Le caselle da cui spedire no — si creano e si cancellano mentre il prodotto gira — quindi non
 * possono stare in una costante. Arrivano nel `configContext` che il catalogo porta con l'azione
 * (`getConfigContext` lato Java), già filtrate per sito e già ridotte alle sole attive.
 *
 * La voce `0` è il **mittente predefinito** della company, ed è l'unica che questo form riconosce
 * per valore: il server ne manda indirizzo e nome, la parola "predefinito" la mette qui perché è
 * presentazione e va tradotta. Le altre voci portano il solo indirizzo, che è tutto ciò che una
 * casella ha.
 *
 * Il **ripiego** compare solo quando la casella scelta non è il predefinito, come l'indirizzo
 * fisso compare solo con la sorgente che lo usa: col predefinito non c'è niente su cui ripiegare.
 *
 * ## CC e BCC sono indirizzi, non sorgenti
 *
 * Sempre digitati, mai derivati dal record: servono a mettere in copia un responsabile o un
 * archivio, che per definizione non dipendono da chi ha fatto scattare l'automazione.
 *
 * ## Cosa finisce nella config
 *
 * `{ recipientSource, subject, body }` più, quando valorizzati, `recipientAddress`,
 * `cc` e `bcc`. L'indirizzo fisso **non sopravvive al cambio di sorgente**: passando a `RECORD`
 * viene rimosso invece di restare come residuo che nessuno vede più ma che il motore leggerebbe.
 *
 * ## I segnaposto, e perché il vocabolario è quello del blocco SE
 *
 * Oggetto e corpo ammettono i `[$nomeCampo$]` che il motore risolve alla spedizione, coi valori
 * congelati al momento dell'evento. Le voci offerte sono i **campi filtrabili** dell'entità
 * (`conditionTypesByClassName`), cioè lo stesso catalogo che il blocco SE già mostra: chi ha
 * scritto la condizione ritrova qui le stesse voci, e il form non chiede al server niente che il
 * builder non abbia già caricato.
 *
 * Un menu per campo e non uno solo: quello del corpo vive nella toolbar dell'editor
 * (`insertables`), quello dell'oggetto accanto al suo campo. Ciascuno sa già dove scrivere,
 * mentre un menu solo dovrebbe ricordare l'ultimo campo toccato — e all'apertura della modale
 * non ne avrebbe ancora uno.
 *
 * Nel CORPO la stessa lista è dietro due pulsanti, che è cosa dell'editor e non di qui: il primo
 * inserisce il solo `[$title$]`, il secondo scrive `Titolo: [$title$]` e va a capo, per le mail
 * che sono un riepilogo di campi invece di una frase. L'oggetto ha il solo pulsante semplice,
 * perché un a capo in un input a riga singola non ha dove andare.
 *
 * Quell'a capo è un `<br>`, e non un `\n`, per una ragione che sta a valle: il corpo si spedisce
 * con `setHTMLFormat(true)` in `SendEmailAutomationActionType`, dove un a capo di testo semplice
 * non è più un a capo — il file Java lo dichiara come prezzo pagato per le automazioni scritte
 * prima dell'editor, che arrivano al destinatario su una riga sola. Un elenco costruito coi `\n`
 * si presenterebbe così, senza che nulla fallisca prima della spedizione.
 *
 * I campi **data** compaiono due volte, con e senza orario (`[$createDate$]` e
 * `[$createDate:datetime$]`): il dato l'ora ce l'ha sempre, e quale delle due forme serva lo sa
 * solo chi sta scrivendo quella frase. Il vocabolario dei modificatori è chiuso — lato motore ogni
 * modificatore è una chiave precalcolata, non una regola da interpretare. Conseguenza sul pulsante
 * etichettato: la voce con orario si scrive `Data creazione (con orario): [$createDate:datetime$]`,
 * col chiarimento del menu dentro la frase. Resta testo modificabile a mano, ed è il prezzo scelto
 * per non far entrare nell'editor una seconda etichetta buona solo per il contenuto.
 *
 * La **sintassi non si dichiara qui**: i delimitatori li compone `placeholderToken`, e sul lato
 * Java vivono in `PLACEHOLDER_PREFIX`/`PLACEHOLDER_SUFFIX` di `SendEmailAutomationActionType`.
 * SYNC CROSS-LAYER: una forma diversa fra i due lati non è un errore che si veda — il segnaposto
 * arriverebbe al destinatario scritto tale e quale, perché nel contesto non lo si troverebbe.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchAutomationConditionTypes } from '../automationThunks';
import { ChipSelect } from '../catalogFieldEditors';
import type { ChipOption } from '../catalogFieldEditors';
import { RichTextEditor } from '../../../components/RichTextEditor';
import type { ActionConfigProps } from './automationActionConfig';

/**
 * Chiavi FACOLTATIVE. Costanti e non letterali perché ciascuna compare in tre punti — lettura,
 * scrittura e cancellazione quando si svuota — e un refuso in uno solo dei tre non fallirebbe la
 * compilazione: produrrebbe una config con una chiave che il provider Java non legge.
 */
const BCC = 'bcc';
const CC = 'cc';
const FALLBACK_TO_DEFAULT_SENDER = 'fallbackToDefaultSender';
const RECIPIENT_ADDRESS = 'recipientAddress';
const REQUIRES_REVIEW = 'requiresReview';
const SENDER_MAILBOX_ID = 'senderMailboxId';

/**
 * Chiave del `configContext` che porta le voci del mittente, e valore della voce predefinita.
 *
 * SYNC CROSS-LAYER: `SENDERS` e `DEFAULT_SENDER` di `SendEmailAutomationActionType`. Sbagliare la
 * prima non è un errore che si veda — la tendina resterebbe vuota come se non ci fossero caselle;
 * sbagliare la seconda farebbe leggere il predefinito come una casella qualsiasi, senza la parola
 * che lo nomina e con un ripiego offerto su se stesso.
 */
const SENDERS = 'senders';
const DEFAULT_SENDER = '0';

/**
 * Le due risposte al ripiego, rese come voci di combo e non come interruttore: la domanda è
 * lunga da scrivere — «se la casella non è più disponibile» — e accanto al chip del mittente,
 * che da quando la casella ha un nome porta «Nome <indirizzo>», un'etichetta di quella lunghezza
 * non ci stava più.
 *
 * Il prefisso è in ENTRAMBE le voci, e la ripetizione nel menu è voluta: il chip mostra la sola
 * voce scelta, e «non spedire» senza il quando direbbe a metà. I valori sono le due stringhe di
 * un booleano perché `ChipSelect` lavora su stringhe; a riportarlo in config è `onChange`.
 */
const FALLBACK_OPTIONS: ChipOption[] = [
  { value: 'false', label: 'automation.form.emailSenderFallbackOff' },
  { value: 'true', label: 'automation.form.emailSenderFallbackOn' },
];

/**
 * «Genera da verificare» (FILO-60), reso come il ripiego: due voci invece di un interruttore. La
 * voce spenta non vuol dire sempre «parte subito»: con l'invio delle mail impostato su Differito la
 * mail resta in Sorveglianza comunque. Per questo ogni voce ha la sua riga d'aiuto, come le sorgenti
 * del destinatario: l'eccezione va detta dove si sceglie, non lasciata da scoprire.
 */
const REVIEW_OPTIONS: { value: string; label: string; help: string }[] = [
  { value: 'false', label: 'automation.form.emailReviewOff', help: 'automation.form.emailReviewOffHelp' },
  { value: 'true', label: 'automation.form.emailReviewOn', help: 'automation.form.emailReviewOnHelp' },
];

type RecipientSource = 'RECORD' | 'RELATED' | 'BOTH' | 'FIXED';

/**
 * SYNC: i `value` sono un vocabolario **condiviso col backend**, non un elenco di questo form. Lato
 * Java sono l'enum {@code AutomationEmailRecipientSource} (`crm-automation-api`, `constants`), che
 * il provider {@code crm.send-email} usa per validare la configurazione e il motore per risolvere i
 * destinatari in indirizzi veri. Aggiungerne uno è per forza una modifica ai due lati — e l'enum
 * non porta etichette di proposito: a nominarli in lingua è questo form, con le proprie chiavi i18n.
 */
const RECIPIENT_SOURCES: { value: RecipientSource; label: string; help: string }[] = [
  { value: 'RECORD', label: 'automation.form.emailRecipientRecord',
    help: 'automation.form.emailRecipientRecordHelp' },
  { value: 'RELATED', label: 'automation.form.emailRecipientRelated',
    help: 'automation.form.emailRecipientRelatedHelp' },
  { value: 'BOTH', label: 'automation.form.emailRecipientBoth',
    help: 'automation.form.emailRecipientBothHelp' },
  { value: 'FIXED', label: 'automation.form.emailRecipientFixed',
    help: 'automation.form.emailRecipientFixedHelp' },
];

const DEFAULT_SOURCE: RecipientSource = 'RECORD';

/**
 * Modificatore che chiede a un campo data di stampare anche l'orario. Vocabolario CHIUSO, e per
 * ora è l'unica voce: lato motore ogni modificatore è una chiave precalcolata nel contesto della
 * mail, quindi ciò che non è elencato qui non si risolve e resterebbe scritto nella mail.
 *
 * SYNC CROSS-LAYER: `DATETIME_MODIFIER` di `SendEmailAutomationActionType`.
 */
const DATETIME_MODIFIER = 'datetime';

/**
 * Il segnaposto di un campo, nella forma che il motore riconosce.
 *
 * SYNC CROSS-LAYER: `PLACEHOLDER_PREFIX` e `PLACEHOLDER_SUFFIX` di
 * `SendEmailAutomationActionType`. Funzione e non due costanti sparse nel JSX perché è l'unico
 * punto del client che quella forma la conosce: il giorno che cambiasse, cambia qui.
 */
const placeholderToken = (fieldName: string, modifier?: string) =>
  `[$${fieldName}${modifier ? `:${modifier}` : ''}$]`;

export default function SendEmailActionConfig({
  className,
  config,
  configContext,
  onChange,
}: ActionConfigProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const conditionTypesByClassName = useAppSelector((s) => s.automations.conditionTypesByClassName);

  // Il catalogo del blocco SE, che qui è il vocabolario dei segnaposto. Lazy per className con la
  // stessa cache che usa il resto del builder: se il SE lo ha già chiesto per questa entità, non
  // parte una seconda richiesta.
  const fields = conditionTypesByClassName[className] ?? [];

  useEffect(() => {
    if (className && !conditionTypesByClassName[className])
      dispatch(fetchAutomationConditionTypes(className));
  }, [className, conditionTypesByClassName, dispatch]);

  // L'input dell'oggetto e il menu che ci scrive dentro. Il ref serve al punto di inserimento:
  // `selectionStart` è l'unico modo per sapere dove sta il cursore in un input controllato.
  const subjectRef = useRef<HTMLInputElement>(null);
  const [subjectAnchorEl, setSubjectAnchorEl] = useState<HTMLElement | null>(null);

  // `as … | undefined` e non un cast secco: la config è opaca (`Record<string, unknown>`), quindi
  // l'asserzione è nostra — dichiararla nullable dice il vero e lascia lavorare il `??`.
  const recipientSource =
    (config?.recipientSource as RecipientSource | undefined) ?? DEFAULT_SOURCE;
  const recipientAddress = (config?.[RECIPIENT_ADDRESS] as string | undefined) ?? '';
  const cc = (config?.[CC] as string | undefined) ?? '';
  const bcc = (config?.[BCC] as string | undefined) ?? '';
  const subject = (config?.subject as string | undefined) ?? '';
  const body = (config?.body as string | undefined) ?? '';

  // Confronto e ChipSelect lavorano su stringhe, la config porta un NUMERO: l'id di una casella è
  // un long lato Java, che lo rilegge con getLong. Si converte qui invece di salvare una stringa,
  // perché è il tipo del dato a decidere come sta a database, non il widget che lo mostra.
  const senderMailboxId = String(config?.[SENDER_MAILBOX_ID] ?? DEFAULT_SENDER);
  const fallbackToDefaultSender = Boolean(config?.[FALLBACK_TO_DEFAULT_SENDER]);
  const requiresReview = Boolean(config?.[REQUIRES_REVIEW]);

  /**
   * Le voci del mittente, dal `configContext` che il catalogo ha portato. Il parse sta qui e non in
   * un mapper comune perché quella forma la conosce solo questa azione, ed è racchiuso in un
   * try/catch: la stringa arriva da un'azione che potrebbe essere di un plugin, e un JSON rotto
   * deve svuotare la tendina, non far cadere il pannello.
   *
   * `useMemo` fa una cosa sola: non riparsare la stringa a ogni render. Non dà identità stabile
   * alle opzioni della tendina — `senderOptions` qui sotto è un `map` fuori dal memo, quindi un
   * array nuovo ogni volta — e non serve a evitare rimontaggi: il Menu del `ChipSelect` vive sul
   * suo `anchorEl` di stato locale, che una prop nuova non tocca.
   */
  const senders = useMemo<ChipOption[]>(() => {
    if (!configContext) return [];

    try {
      const parsed = JSON.parse(configContext) as { senders?: ChipOption[] };

      return parsed[SENDERS] ?? [];
    } catch {
      return [];
    }
  }, [configContext]);

  // La parola "predefinito" la mette il CLIENT, e solo sulla voce che il server ha marcato con lo
  // zero: il server manda indirizzo e nome del mittente di company, cioè il dato, e come chiamarlo
  // dipende dalla lingua di chi guarda. Le altre voci si leggono come il server le compone: nome
  // e indirizzo della casella, o il solo indirizzo per quelle configurate prima che il nome
  // esistesse.
  const senderOptions = senders.map((sender) => ({
    value: sender.value,
    label:
      sender.value === DEFAULT_SENDER
        ? t('automation.form.emailSenderDefault', { label: sender.label })
        : sender.label,
  }));

  // La voce corrente si cerca nell'elenco invece di indicizzare una mappa: `recipientSource` viene
  // dalla config, che è opaca, quindi può portare un codice che l'elenco non ha — di un'automazione
  // vecchia, o scritto a mano. Il fallback sulla prima voce tiene in piedi il pannello invece di
  // lasciarlo senza spiegazione.
  const selectedRecipientSource =
    RECIPIENT_SOURCES.find((source) => source.value === recipientSource) ?? RECIPIENT_SOURCES[0];

  /**
   * La config si riscrive INTERA a ogni modifica: è il form l'unico a conoscerne la forma, e
   * restituirne una parziale obbligherebbe chi lo ospita a saperla ricomporre. Parte da quella
   * corrente per non buttare via chiavi che questo form non gestisce.
   */
  const emit = (next: Record<string, unknown>) => {
    const merged: Record<string, unknown> = {
      ...(config ?? {}),
      recipientSource,
      [RECIPIENT_ADDRESS]: recipientAddress,
      [CC]: cc,
      [BCC]: bcc,
      [SENDER_MAILBOX_ID]: Number(senderMailboxId),
      [FALLBACK_TO_DEFAULT_SENDER]: fallbackToDefaultSender,
      [REQUIRES_REVIEW]: requiresReview,
      subject,
      body,
      ...next,
    };

    // L'indirizzo fisso appartiene alla sola sorgente FIXED: tornando a RECORD resterebbe in
    // configurazione senza più un campo che lo mostri — invisibile a chi rilegge e leggibile dal
    // motore, che è la combinazione peggiore.
    if (merged.recipientSource !== 'FIXED') delete merged[RECIPIENT_ADDRESS];

    // Il ripiego appartiene alla sola scelta che lo può usare: tornando al mittente predefinito
    // resterebbe in configurazione senza più un campo che lo mostri, e direbbe di ripiegare su
    // quello che è già stato scelto. Stessa regola dell'indirizzo fisso qui sopra.
    if (merged[SENDER_MAILBOX_ID] === Number(DEFAULT_SENDER))
      delete merged[FALLBACK_TO_DEFAULT_SENDER];

    // Chiavi facoltative OMESSE invece che vuote: una stringa vuota a database si legge come un
    // indirizzo che c'è ed è sbagliato, l'assenza come un campo non compilato.
    // NON aggiungere qui SENDER_MAILBOX_ID: il mittente predefinito è lo zero, che è falsy, e
    // questo ciclo lo cancellerebbe come se fosse un campo non compilato. Lato motore l'assenza
    // vale comunque predefinito, quindi non si romperebbe niente — si perderebbe la differenza
    // fra "ha scelto il predefinito" e "non ha scelto", che è l'unica ragione per cui lo zero
    // viene scritto. Le tre chiavi qui elencate sono testuali: per loro vuoto e assente sono la
    // stessa cosa, per un id no.
    for (const key of [RECIPIENT_ADDRESS, CC, BCC]) {
      if (!merged[key]) delete merged[key];
    }

    onChange(merged);
  };

  // Ordinamento alfabetico per etichetta localizzata, come per i campi del SE: dipende dalla
  // locale, che conosce il client. Copia prima di ordinare — `fields` viene dallo store e non si
  // muta.
  // Le DATE offrono due voci, subito una sotto l'altra: il dato l'orario ce l'ha sempre —
  // createDate e modifiedDate sono istanti — ed è chi scrive la frase a sapere se in quel punto
  // serve. Le due restano vicine perché sono lo stesso campo detto in due modi, non due campi.
  const placeholderItems = [...fields]
    .sort((a, b) => a.label.localeCompare(b.label))
    .flatMap((field) => {
      const item = { value: placeholderToken(field.fieldName), label: field.label };

      if (field.type !== 'DATE') return [item];

      return [
        item,
        {
          value: placeholderToken(field.fieldName, DATETIME_MODIFIER),
          label: t('automation.form.placeholderWithTime', { label: field.label }),
        },
      ];
    });

  /**
   * Inserisce un segnaposto nell'OGGETTO al punto del cursore, sostituendo il testo selezionato
   * se ce n'è. Senza un cursore noto si accoda, che è dove se lo aspetta chi il campo non lo ha
   * ancora toccato.
   */
  const insertIntoSubject = (token: string) => {
    setSubjectAnchorEl(null);

    const input = subjectRef.current;
    const start = input?.selectionStart ?? subject.length;
    const end = input?.selectionEnd ?? subject.length;

    emit({ subject: subject.slice(0, start) + token + subject.slice(end) });

    // Il cursore DOPO il segnaposto appena messo, e non a fine campo: l'input è controllato,
    // quindi il valore nuovo arriva col render successivo — riposizionarlo adesso agirebbe su
    // quello vecchio, e chi sta scrivendo una frase si ritroverebbe il cursore altrove.
    requestAnimationFrame(() => {
      const caret = start + token.length;

      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="builder-grid">
      {/* Se la mail parte o aspetta una verifica, PER PRIMA: è la scelta che cambia la natura
          dell'azione — una mail che parte da sola o una bozza per un operatore — e chi configura
          deve averla davanti prima di scrivere il resto. Sempre visibile: a differenza del ripiego
          non dipende da un'altra scelta. */}
      <div className="builder-crow builder-crow--wide">
        <span className="text-sm text-muted">{t('automation.form.emailReview')}</span>
        <span className="builder-inline">
          <ChipSelect
            value={String(requiresReview)}
            options={REVIEW_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }))}
            ariaLabel={t('automation.form.emailReview')}
            onChange={(next) => emit({ [REQUIRES_REVIEW]: next === 'true' })}
          />
        </span>
      </div>
      <div className="builder-crow builder-crow--help">
        <span className="builder-crow__help">
          {t(requiresReview ? REVIEW_OPTIONS[1].help : REVIEW_OPTIONS[0].help)}
        </span>
      </div>

      {/* Il mittente PRIMA del destinatario, che è l'ordine in cui si legge una mail — e prima
          dell'oggetto, perché da chi parte cambia come si scrive quello che segue.
          La guardia non è sul contesto VUOTO, che non esiste — il predefinito c'è sempre, lo
          mette `getConfigContext` senza condizioni — ma sul contesto ASSENTE: un catalogo non
          ancora caricato, o un backend che il campo non lo manda ancora. In quel caso non si
          mostra nulla, e le automazioni spediscono dal predefinito come hanno sempre fatto. */}
      {senderOptions.length > 0 && (
        <>
          <div className="builder-crow builder-crow--wide">
            <span className="text-sm text-muted">{t('automation.form.emailSender')}</span>
            <span className="builder-inline">
              {/* Il PLACEHOLDER non è un dettaglio di cortesia: il catalogo porta le sole caselle
                  ATTIVE, quindi una casella disattivata dopo la configurazione non è fra le voci e
                  `ChipSelect` mostrerebbe un chip con la sola freccia. È uno stato di regime e non
                  un'eccezione — è la ragione per cui esiste `fallbackToDefaultSender` — e detto
                  così spiega anche perché la riga del ripiego qui sotto compaia. Quale indirizzo
                  fosse non si può dire: il catalogo non lo porta più, e questo form non ha altra
                  fonte. */}
              <ChipSelect
                value={senderMailboxId}
                options={senderOptions}
                placeholder={t('automation.form.emailSenderUnavailable')}
                ariaLabel={t('automation.form.emailSender')}
                onChange={(next) => emit({ [SENDER_MAILBOX_ID]: Number(next) })}
              />
            </span>
          </div>

          {/* Il ripiego su una RIGA PROPRIA, e non accanto al mittente: da quando la casella ha un
              nome il primo chip porta «Nome <indirizzo>», e due pastiglie lunghe nella stessa cella
              si stringevano a vicenda. La riga parte dalla colonna del campo — è ciò che fa
              `builder-crow--help` — perché parla della scelta fatta qui sopra, non di un campo suo.
              Compare solo con una casella scelta: sul predefinito non c'è niente su cui
              ripiegare, e offrirlo chiederebbe una risposta a una domanda che non esiste. */}
          {senderMailboxId !== DEFAULT_SENDER && (
            <div className="builder-crow builder-crow--help">
              <ChipSelect
                value={String(fallbackToDefaultSender)}
                options={FALLBACK_OPTIONS.map((option) => ({
                  value: option.value,
                  label: t(option.label),
                }))}
                ariaLabel={t('automation.form.emailSenderFallback')}
                onChange={(next) => emit({ [FALLBACK_TO_DEFAULT_SENDER]: next === 'true' })}
              />
            </div>
          )}
        </>
      )}

      <div className="builder-crow builder-crow--wide">
        <span className="text-sm text-muted">{t('automation.form.recipient')}</span>
        <span className="builder-inline">
          <ChipSelect
            value={recipientSource}
            options={RECIPIENT_SOURCES.map((source) => ({
              value: source.value,
              label: t(source.label),
            }))}
            ariaLabel={t('automation.form.recipient')}
            onChange={(next) => {
              // La voce scelta si ritrova nell'elenco invece di essere convertita con un cast: le
              // opzioni nascono da RECIPIENT_SOURCES, quindi il valore tipizzato ce l'ha già
              // l'elemento trovato, e un codice sconosciuto viene ignorato invece che persistito.
              const picked = RECIPIENT_SOURCES.find((source) => source.value === next);

              if (picked) emit({ recipientSource: picked.value });
            }}
          />

          {/* L'indirizzo compare solo con la sorgente che lo usa, come i campi di retry compaiono
              solo con RETRY: un campo inerte accanto a una scelta che lo ignora chiede di capire da
              sé che non conta. */}
          {recipientSource === 'FIXED' && (
            <span className="builder-fieldnum builder-fieldnum--grow">
              <input
                type="email"
                value={recipientAddress}
                aria-label={t('automation.form.emailAddress')}
                placeholder={t('automation.form.emailAddress')}
                onChange={(e) => emit({ [RECIPIENT_ADDRESS]: e.target.value })}
              />
            </span>
          )}
        </span>
      </div>

      {/* Che cosa significhi la voce scelta, SEMPRE VISIBILE e non in un fumetto: il significato
          delle sorgenti dipende dall'entità — "Il record" su un preventivo è il suo cliente, non il
          preventivo — e non è deducibile dall'etichetta. Un tooltip lo direbbe solo a chi passa il
          mouse, cioè non a chi usa la tastiera e non su touch.
          Cambia con la voce selezionata invece di elencarle tutte: chi sta configurando ha una
          domanda sola, cosa fa QUESTA scelta, e le altre tre risposte sarebbero rumore. Si scoprono
          spostandosi fra i chip, che è il gesto che uno fa comunque per scegliere. */}
      <div className="builder-crow builder-crow--help">
        <span className="builder-crow__help">{t(selectedRecipientSource.help)}</span>
      </div>

      {/* Il segnaposto dichiara il SEPARATORE, e non è un dettaglio di cortesia: quanti indirizzi
          si possono scrivere e come vadano divisi è un accordo fra questo campo e il motore che
          poi li spedisce, e senza scriverlo da qualche parte resterebbe da indovinare per
          entrambi. Il posto dove si vede è il campo stesso. */}
      <div className="builder-crow builder-crow--wide">
        <span className="text-sm text-muted">{t('automation.form.emailCc')}</span>
        <span className="builder-fieldnum builder-fieldnum--grow">
          <input
            type="text"
            value={cc}
            aria-label={t('automation.form.emailCc')}
            placeholder={t('automation.form.emailAddressListPlaceholder')}
            onChange={(e) => emit({ [CC]: e.target.value })}
          />
        </span>
      </div>

      <div className="builder-crow builder-crow--wide">
        <span className="text-sm text-muted">{t('automation.form.emailBcc')}</span>
        <span className="builder-fieldnum builder-fieldnum--grow">
          <input
            type="text"
            value={bcc}
            aria-label={t('automation.form.emailBcc')}
            placeholder={t('automation.form.emailAddressListPlaceholder')}
            onChange={(e) => emit({ [BCC]: e.target.value })}
          />
        </span>
      </div>

      {/* Il menu dei segnaposto accanto all'oggetto, e non dentro il campo: l'oggetto è un input
          semplice e una toolbar non ce l'ha, mentre il corpo il suo pulsante lo ha nella toolbar
          dell'editor. Compare solo se il catalogo ha voci — appena aperta la modale è ancora in
          volo, e un menu vuoto chiederebbe di capire da sé che non è colpa propria. */}
      <div className="builder-crow builder-crow--wide">
        <span className="text-sm text-muted">{t('automation.form.subject')}</span>
        <span className="builder-inline">
          <span className="builder-fieldnum builder-fieldnum--grow">
            <input
              ref={subjectRef}
              type="text"
              value={subject}
              aria-label={t('automation.form.subject')}
              onChange={(e) => emit({ subject: e.target.value })}
            />
          </span>
          {placeholderItems.length > 0 && (
            <IconButton
              size="small"
              title={t('automation.form.insertField')}
              aria-label={t('automation.form.insertField')}
              onClick={(e) => setSubjectAnchorEl(e.currentTarget)}
            >
              <DataObjectIcon fontSize="small" />
            </IconButton>
          )}
        </span>
      </div>

      <Menu
        anchorEl={subjectAnchorEl}
        open={Boolean(subjectAnchorEl)}
        onClose={() => setSubjectAnchorEl(null)}
      >
        {placeholderItems.map((item) => (
          <MenuItem key={item.value} onClick={() => insertIntoSubject(item.value)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Il corpo esce dallo schema etichetta-a-sinistra e prende la riga intera: l'editor porta
          una toolbar affollata (i pulsanti sono quelli di `RichTextEditor`, che ne aggiunge nel
          tempo), e nella seconda colonna diventerebbe una colonnina con i pulsanti a capo. Senza
          etichetta a fianco, perché a dire che campo è bastano il segnaposto e la toolbar: una
          scritta "Corpo" sopra ripeterebbe quella che si legge dentro finché il campo è vuoto.
          NON controllato, a differenza degli altri campi: il contenuto vive nel DOM del div
          editabile e l'editor emette l'HTML a ogni battuta. `initialHtml` si legge una volta sola,
          al mount, e qui basta: AutomationForm monta la modale solo mentre un'azione è in modifica,
          quindi ogni apertura è un montaggio nuovo e nessuna `key` deve forzarlo.
          L'editor porta la propria cornice (`.rte` ha bordo, raggio e sfondo), quindi non si
          avvolge in una pastiglia: sarebbe un riquadro dentro un riquadro.
          Nessun `mentionSearch`: le menzioni scrivono token [~utente] che hanno senso nei commenti
          interni, non in una mail che esce dal CRM. */}
      <div className="builder-crow builder-crow--full builder-crow--editor">
        <RichTextEditor
          initialHtml={body}
          onChange={(html) => emit({ body: html })}
          placeholder={t('automation.form.emailBody')}
          insertables={placeholderItems}
          insertablesLabel={t('automation.form.insertField')}
        />
      </div>
    </div>
  );
}
